import { useMemo, useRef, useState } from "react";
import { useAuth } from "@/auth/useAuth";
import { cardRefundMutationToken } from "@/auth/cardRefundAuthentication";
import { createDeletionCommandRunner } from "@/api/deletionCommand";
import { sendCardRefundCommand, type CardRefundCommand } from "@/api/cardRefunds";
import { AdminApiError } from "@/api/client";
import { env } from "@/config/env";

export function useCardRefundCommand() {
  const { user, claims, capabilities } = useAuth();
  const uid = user?.uid ?? "";
  const operatorId = claims?.backendId ?? "";
  // Reuse the existing command-identity primitive. Accepted evidence is persisted by the server.
  const runner = useMemo(() => createDeletionCommandRunner(uid), [uid]);
  const inFlight = useRef(false);
  const [pending, setPending] = useState(false);
  const canAct = !!uid && !!operatorId && capabilities.act && (!env.isProd || capabilities.actProd);

  async function execute(id: string, updatedAt: string, command: CardRefundCommand) {
    if (!canAct) throw new Error("Your role cannot process card refunds.");
    if (inFlight.current) throw new Error("An action is already in progress.");
    inFlight.current = true;
    setPending(true);
    try {
      return await runner(
        {
          actor: uid,
          target: `cardRefund:${id}:${command.action}`,
          updatedAt,
          input: command.input,
        },
        () => cardRefundMutationToken(uid, operatorId),
        (token, requestId) => sendCardRefundCommand(token, id, updatedAt, requestId, command),
      );
    } catch (error) {
      if (error instanceof AdminApiError && error.conflict) {
        throw new Error(
          "This refund or its instructions changed. Refresh, review the latest case, and reopen the action. If a transfer was sent, keep its hash; do not send again.",
        );
      }
      throw error;
    } finally {
      inFlight.current = false;
      setPending(false);
    }
  }
  return { execute, pending, canAct, operatorId };
}
